// /auth/accept-invite — exchange a magic-link token for a JWT.

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Notice } from "../components/RecordUI";
import AuthShell from "../layouts/AuthShell";
import { auth, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setError("No invite token in the URL.");
      return;
    }
    // If the magic-link URL also carries ?ref=<code>, pass it through so
    // the new tenant gets stamped with the referrer at activation time.
    const referralCode = params.get("ref") ?? undefined;
    (async () => {
      try {
        const tok = await auth.acceptInvite(token, referralCode);
        signIn(tok);
        navigate("/", { replace: true });
      } catch (e: unknown) {
        setError(e instanceof ApiError ? e.message : "Invite verification failed");
      }
    })();
  }, [params, signIn, navigate]);

  return (
    <AuthShell>
      {error ? (
        <div className="space-y-4">
          <h1 className="font-display text-h2 text-ink">We couldn't open that invite</h1>
          <Notice tone="error">{error}</Notice>
          <p className="text-small text-ink-2">
            Invite links expire after a while. Request a fresh one from the sign-in page.
          </p>
          <Link to="/sign-in" className="btn-primary w-full whitespace-nowrap">
            Go to sign in
          </Link>
        </div>
      ) : (
        <div className="flex items-center gap-3" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-terra-600" aria-hidden />
          <p className="text-body text-ink-1">Signing you in…</p>
        </div>
      )}
    </AuthShell>
  );
}
